/**
 * Line Connect Log Service
 * 
 * Service สำหรับจัดการข้อมูล Line Connect Log
 */

import { queryClass } from '@/services/adapters/parse/parse-client'
import type {
  LineConnectLog,
  LineConnectLogQueryParams,
  LineConnectLogQueryResult,
} from './line-connect-log.types'

// ============================================
// Mapping Functions
// ============================================

function mapToLineConnectLog(raw: any): LineConnectLog {
  return {
    id: raw.objectId,
    objectId: raw.objectId,
    
    // Line Data
    lineId: raw.line_id,
    lineType: raw.line_type,
    title: raw.title,
    msg: raw.msg,
    webbankPhoneNumber: raw.webbank_phone_number,
    
    // Transaction Data
    date: raw.date,
    time: raw.time,
    dateTime: raw.date_time,
    dateTimeFormat: raw.date_time_format,
    type: raw.type,
    amount: raw.amount || 0,
    
    // Bank Info
    myNumber: raw.my_number,
    bank: raw.bank,
    bankNumber: raw.bank_number,
    
    // Status
    status: raw.status,
    used: raw.used,
    
    // Raw Data
    dataObj: raw.data_obj,
    
    // Timestamps
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  }
}

// ============================================
// Service
// ============================================

export const LineConnectLogService = {
  /**
   * ดึงรายการ Line Connect Log
   */
  async getAll(
    sessionToken: string,
    params: LineConnectLogQueryParams = {}
  ): Promise<LineConnectLogQueryResult> {
    try {
      const {
        page = 1,
        limit = 50,
        status = 'all',
        type = 'all',
        used = 'all',
        startDate,
        endDate,
        bank,
        phoneNumber,
      } = params

      const queryOptions: any = {
        where: {},
        limit,
        skip: (page - 1) * limit,
        order: '-createdAt', // ล่าสุดก่อน
      }

      // Filters
      if (status !== 'all') {
        queryOptions.where.status = status
      }

      if (type !== 'all') {
        queryOptions.where.type = type
      }

      if (used !== 'all') {
        queryOptions.where.used = used === true
      }

      // ✅ ใช้ date_time_format สำหรับ filter เพราะเป็นวันที่เวลาของธุรกรรมจริง (ISO format)
      // ✅ date_time_format เป็น ISO string (เช่น "2026-01-05T03:51:00")
      // ✅ createdAt เป็นวันที่สร้าง record ซึ่งอาจจะต่างจากวันที่ธุรกรรมจริง
      if (startDate) {
        const startDateObj = new Date(startDate);
        // ✅ Format เป็น ISO string โดยตัด timezone ออก (เหมือน date_time_format ใน DB)
        const startDateStr = startDateObj.toISOString().replace(/([+-]\d{2}:\d{2})$/, '');
        queryOptions.where.date_time_format = {
          ...queryOptions.where.date_time_format,
          $gte: startDateStr,
        }
      }

      if (endDate) {
        const endDateObj = new Date(endDate);
        // ✅ Format เป็น ISO string โดยตัด timezone ออก (เหมือน date_time_format ใน DB)
        const endDateStr = endDateObj.toISOString().replace(/([+-]\d{2}:\d{2})$/, '');
        queryOptions.where.date_time_format = {
          ...queryOptions.where.date_time_format,
          $lte: endDateStr,
        }
      }
      
      console.log('📅 [LineConnectLogService] Date filter:', {
        startDate,
        endDate,
        whereDateTimeFormat: queryOptions.where.date_time_format,
      });

      if (bank) {
        queryOptions.where.bank = bank
      }

      if (phoneNumber) {
        queryOptions.where.webbank_phone_number = phoneNumber
      }

      const response = await queryClass('LineConnectLog', queryOptions, sessionToken)

      return {
        results: (response.results || []).map(mapToLineConnectLog),
        total: response.count || 0,
        page,
        limit,
      }
    } catch (error) {
      console.error('❌ [LineConnectLogService] getAll failed:', error)
      throw error
    }
  },
}

